import { type DragEvent, useCallback, useEffect, useRef } from "react"
import { type TableContainerProps } from "@mui/material"

// Distance (px) from the container's top/bottom edge that triggers auto-scroll.
const EDGE_SIZE = 60
// Maximum scroll step (px) applied per animation frame at the very edge.
const MAX_SPEED = 18

type DragAutoScrollProps = Pick<TableContainerProps, "onDragOver" | "onDrop">

/**
 * material-react-table v1.8.5 row reordering uses native HTML5 drag-and-drop and
 * has no auto-scroll, so a row can't be dropped above/below the visible area.
 *
 * This hook scrolls the table's scroll container while the pointer sits within an
 * edge band during a drag. The rAF loop keeps scrolling even when the pointer is
 * held still at the edge (which `dragover` alone wouldn't do, as it stops firing).
 */
export function useTableDragAutoScroll(): DragAutoScrollProps {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const speedRef = useRef(0)
    const frameRef = useRef<number | null>(null)

    const stop = useCallback(() => {
        speedRef.current = 0
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current)
            frameRef.current = null
        }
    }, [])

    const tick = useCallback(() => {
        const el = containerRef.current
        if (el && speedRef.current !== 0) {
            el.scrollTop += speedRef.current
            frameRef.current = requestAnimationFrame(tick)
        } else {
            frameRef.current = null
        }
    }, [])

    const onDragOver = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            const el = event.currentTarget
            containerRef.current = el

            const rect = el.getBoundingClientRect()
            const distanceFromTop = event.clientY - rect.top
            const distanceFromBottom = rect.bottom - event.clientY

            if (distanceFromTop < EDGE_SIZE) {
                speedRef.current = -MAX_SPEED * (1 - Math.max(distanceFromTop, 0) / EDGE_SIZE)
            } else if (distanceFromBottom < EDGE_SIZE) {
                speedRef.current = MAX_SPEED * (1 - Math.max(distanceFromBottom, 0) / EDGE_SIZE)
            } else {
                speedRef.current = 0
            }

            if (speedRef.current !== 0 && frameRef.current === null) {
                frameRef.current = requestAnimationFrame(tick)
            }
        },
        [tick],
    )

    // Stop scrolling whenever a drag ends anywhere (the drop may land outside the
    // container), and clean up any pending frame on unmount.
    useEffect(() => {
        window.addEventListener("dragend", stop)
        return () => {
            window.removeEventListener("dragend", stop)
            stop()
        }
    }, [stop])

    return { onDragOver, onDrop: stop }
}

